import React from 'react';
import Type from 'prop-types';
import { noop } from 'lodash';
import {
  Badge,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  ListSubheader,
  Radio,
  Typography,
} from '@mui/material';
import { useAutocomplete, useTranslation } from 'hooks';
import { getInitials } from 'utils/string';
import { t as translateFn } from 'utils/i18n';
import Transition from '../../../../Transition';
import FilterDialogActions from '../../../../Feedback/FilterDialog/FilterDialogActions';
import Avatar from '../../../../Avatar';
import EmptyState from '../../../../EmptyState';
import ActivityIndicator from '../../../../ActivityIndicator';
import SearchBar from '../../../../Form/SearchInput';
import styles from './SearchView.module.css';

const SearchView = (props) => {
  const { t } = useTranslation();
  const {
    inputValue: inputValueProp,
    value: selectedOptionsProp,
    getAvatarUrl,
    getOptionLabel,
    getOptionSelected,
    options,
    groupBy,
    freeSolo,
    loading,
    ListboxComponent,
    ListboxProps,
    multiple,
    showAvatar,
    emptyResults,
    noOptionsText,
    open,
    onClose,
    onInputClear,
    onInputChange,
    onSearch,
    onApply,
    renderGroup: renderGroupProp,
    renderOption: renderOptionProp,
    children,
    ...other
  } = props;

  const hasLabel = (option) => {
    const label = option == null ? '' : getOptionLabel(option);

    return typeof label === 'string' && label.trim().length > 0;
  };

  const validSelectedOptions = selectedOptionsProp.filter(hasLabel);
  const validOptions = options.filter(hasLabel);

  const compareOptions = (option1, option2) => {
    if (groupBy) {
      const groupComparison = String(groupBy(option1)).localeCompare(
        String(groupBy(option2)),
      );

      if (groupComparison !== 0) {
        return groupComparison;
      }
    }

    return getOptionLabel(option1).localeCompare(
      getOptionLabel(option2),
      'ru',
      {
        sensitivity: 'base',
      },
    );
  };

  const sortedOptions = [
    ...validSelectedOptions,
    ...validOptions.filter(
      (option) =>
        !validSelectedOptions.some((selectedOption) =>
          getOptionSelected(option, selectedOption),
        ),
    ),
  ].sort(compareOptions);

  const {
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    inputValue,
    value: selectedOptions,
  } = useAutocomplete({
    id: 'search-view',
    open,
    options: sortedOptions,
    multiple,
    freeSolo,
    groupBy,
    filterSelectedOptions: false,
    blurOnSelect: true,
    getOptionLabel,
    inputValue: inputValueProp,
    onInputChange,
    getOptionSelected,
    value: validSelectedOptions,
    ...other,
  });

  const groupedCount = groupedOptions.length;

  const handleValuesReset = (event) => {
    event.stopPropagation();
    const { onChange } = props;

    onChange(event, []);
  };

  const renderResetButton = () => {
    return (
      <Button color="primary" onClick={handleValuesReset}>
        {t('reset')}
      </Button>
    );
  };

  const renderSearchBar = () => {
    return (
      <SearchBar
        className={styles.searchBar}
        InputProps={getInputProps()}
        value={inputValue}
        onSearch={onSearch}
        onIconClick={onClose}
        onClear={onInputChange}
        placeholder={t('search')}
        showDivider
        fullWidth
        visible
        focusOnOpen={false}
      />
    );
  };

  const renderAvatar = (option) => {
    const optionLabel = getOptionLabel(option);
    const initials = getInitials(optionLabel);
    const avatarUrl = getAvatarUrl(option);

    return (
      <div className={styles.optionAvatar}>
        <Avatar src={avatarUrl} alt={option.name}>
          {initials}
        </Avatar>
      </div>
    );
  };

  const defaultRenderOption = (option, state) => {
    const SecondaryAction = multiple ? Checkbox : Radio;

    return (
      <>
        <div className={styles.optionContent}>
          {showAvatar && renderAvatar(option)}

          <div className={styles.optionText}>
            <Typography component="div" variant="body1">
              {getOptionLabel(option)}
            </Typography>
          </div>
        </div>
        <div className={styles.secondaryAction}>
          <SecondaryAction
            color="primary"
            edge="end"
            checked={state.selected}
          />
        </div>
      </>
    );
  };

  const defaultRenderGroup = (params) => {
    return (
      <li key={params.key} className={styles.optionGroup}>
        <ListSubheader
          className={styles.groupLabel}
          component="div"
          disableSticky
        >
          {params.group}
        </ListSubheader>
        <ul className={styles.groupUl}>{params.children}</ul>
      </li>
    );
  };

  const renderGroup = renderGroupProp || defaultRenderGroup;
  const renderOption = renderOptionProp || defaultRenderOption;

  const renderListOption = (option, index) => {
    const optionProps = getOptionProps({ option, index });
    const selected = optionProps['aria-selected'];
    return (
      <li {...optionProps} className={styles.option}>
        {renderOption(option, {
          selected,
          inputValue,
          multiple,
        })}
      </li>
    );
  };

  const renderSelectedOption = (option, index) => {
    const { key, onDelete } = getTagProps({ index });

    return (
      <li key={key} className={styles.selectedItem}>
        <Chip
          className={styles.selectedChip}
          color="primary"
          label={getOptionLabel(option)}
          onDelete={onDelete}
        />
      </li>
    );
  };

  const renderSelectedOptions = () => {
    const selectedCount = selectedOptions.length;

    if (selectedCount === 0) {
      return null;
    }

    return (
      <>
        <section className={styles.selectedSection}>
          <div className={styles.selectedHeader}>
            <ListSubheader
              className={styles.groupLabel}
              component="div"
              disableGutters
            >
              <Badge
                badgeContent={selectedCount}
                color="primary"
                classes={{ root: styles.counter, badge: styles.badge }}
              >
                {t('selected')}
              </Badge>
            </ListSubheader>

            {renderResetButton()}
          </div>
          <ul className={styles.selectedList}>
            {selectedOptions.map(renderSelectedOption)}
          </ul>
        </section>
      </>
    );
  };

  const renderOptions = () => {
    return (
      <ListboxComponent
        className={styles.listbox}
        {...getListboxProps()}
        {...ListboxProps}
      >
        {groupBy
          ? groupedOptions.map((option, index) => {
              return renderGroup({
                index,
                key: option.key,
                group: option.group,
                children: option.options.map((option2, index2) =>
                  renderListOption(option2, option.index + index2),
                ),
              });
            })
          : renderGroup({
              index: 1,
              key: 'all',
              group: t('all'),
              children: groupedOptions.map((option, index) =>
                renderListOption(option, index),
              ),
            })}
      </ListboxComponent>
    );
  };

  const renderEmptyState = () => {
    const emptyText = inputValue.length > 0 ? emptyResults : noOptionsText;

    return <EmptyState title={emptyText} />;
  };

  const isEmptyState = !loading && groupedCount === 0;

  const renderContent = () => {
    if (loading) {
      return <ActivityIndicator />;
    }

    return isEmptyState ? renderEmptyState() : renderOptions();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullScreen
    >
      {renderSearchBar()}

      <DialogContent classes={{ root: styles.dialogContent }} sx={{ px: '16px' }}>
        {renderSelectedOptions()}
        {children}
        {renderContent()}
      </DialogContent>

      <FilterDialogActions onApply={onApply} />
    </Dialog>
  );
};

/**
 * See [Autocomplete](https://material-ui.com/api/autocomplete/)
 * all available autocomplete option
 */
SearchView.propTypes = {
  inputValue: Type.string,
  value: Type.arrayOf(Type.oneOfType([Type.string, Type.object])),
  options: Type.arrayOf(Type.oneOfType([Type.string, Type.object])),
  open: Type.bool,
  ListboxComponent: Type.elementType,
  ListboxProps: Type.object, // eslint-disable-line react/forbid-prop-types
  showAvatar: Type.bool,
  multiple: Type.bool,
  freeSolo: Type.bool,
  clearOnBlur: Type.bool,
  loading: Type.bool,
  disableCloseOnSelect: Type.bool,
  noOptionsText: Type.string,
  emptyResults: Type.string,
  children: Type.node,
  groupBy: Type.func,
  getAvatarUrl: Type.func,
  getOptionLabel: Type.func,
  getOptionSelected: Type.func,
  renderGroup: Type.func,
  renderOption: Type.func,
  onInputClear: Type.func,
  onInputChange: Type.func,
  onChange: Type.func,
  onClose: Type.func,
  onSearch: Type.func,
  onApply: Type.func,
};

SearchView.defaultProps = {
  inputValue: '',
  value: [],
  options: [],
  open: false,
  showAvatar: false,
  multiple: true,
  freeSolo: false,
  clearOnBlur: false,
  loading: false,
  children: null,
  emptyResults: `${translateFn('noResults')}. ${translateFn(
    'tryChangeSearchParams',
  )}`,
  noOptionsText: translateFn('tryStartTypingText'),
  disableCloseOnSelect: true,
  ListboxComponent: 'ul',
  ListboxProps: undefined,
  getAvatarUrl: noop,
  groupBy: undefined,
  renderGroup: undefined,
  renderOption: undefined,
  getOptionLabel: noop,
  getOptionSelected: (option1, option2) => option1 === option2,
  onChange: noop,
  onInputChange: noop,
  onInputClear: noop,
  onClose: noop,
  onSearch: noop,
  onApply: noop,
};

export default SearchView;
